import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import React, { useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  createRequestInfoInApolloOperation,
  addRequestInfoToErrorResponse,
} from '../../../hooks/errorHandlingRequestInfo';
import apolloCacheConfig from './apolloCacheConfig';
import { getApiBaseUrl } from '../../../utils/config';
import { getCurrentLocale } from '../../../context/I18n';
import { refreshAccessTokenSinglePromise } from '../../../api/jwtAuth/jwtAuthLogic';

const devMode = import.meta.env.MODE !== 'production';

export const createRefreshJwtTokenLink = (logout: () => void) =>
  new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      // Once the caller unsubscribes (navigation, component unmount, logout),
      // nothing below may keep running: a surviving retry timer would re-fire
      // the request and could end up calling `logout()` on a session that has
      // already moved on.
      let cancelled = false;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      let innerSubscription: { unsubscribe: () => void } | undefined;

      const handleRequest = (retryCount = 0) => {
        // infinity loop protection
        if (retryCount > 3) {
          logout();
          return;
        }
        innerSubscription = forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (error) => {
            if (error.statusCode === 403) {
              // force logout if 403
              logout();
              return;
            }
            if (error.statusCode === 401) {
              refreshAccessTokenSinglePromise()
                .then((apiToken) => {
                  if (cancelled) {
                    return;
                  }
                  // Retry the request with the new token
                  if (apiToken) {
                    // `retryCount++` here would pass the unchanged value into
                    // the recursive call, so the guard above never trips and a
                    // client that keeps getting 401 after a "successful"
                    // refresh retries forever at machine speed. The growing
                    // delay keeps even the bounded retries from hammering the
                    // server back-to-back.
                    retryTimer = setTimeout(
                      () => handleRequest(retryCount + 1),
                      500 * (retryCount + 1),
                    );
                  } else {
                    // refresh token is expired -> logout
                    logout();
                  }
                })
                .catch((refreshError) => {
                  console.error(
                    'Error while refreshing token on retry',
                    refreshError,
                  );
                  if (!cancelled) {
                    logout();
                  }
                  return;
                });
            } else {
              observer.error(error);
            }
          },
          complete: () => observer.complete(),
        });
      };

      handleRequest();

      return () => {
        cancelled = true;
        if (retryTimer !== undefined) {
          clearTimeout(retryTimer);
        }
        innerSubscription?.unsubscribe();
      };
    });
  });

// `logout` and `getLocale` are read lazily so the client can be built exactly
// once. Rebuilding the client creates a brand-new InMemoryCache, which forces
// every mounted query to refetch over the network — the dominant driver of
// redundant Salesforce traffic, since the employee's language is applied right
// after login and used to flip the locale the client depended on. `getLocale`
// is read per request (see below), so a language change never needs a rebuild.
const buildClient = (logout: () => void, getLocale: () => string) =>
  new ApolloClient({
    name: 'web',
    version: import.meta.env.VITE_APP_CODE_VERSION,
    connectToDevTools: true,
    link: ApolloLink.from([
      new ApolloLink((operation, forward) => {
        createRequestInfoInApolloOperation(operation);
        // Read the locale per request straight from i18next (getLocale) rather
        // than from a React value, so it reflects the current language even
        // though this link is created once and `useCurrentLocale` does not
        // subscribe to language changes.
        operation.setContext(
          ({ headers = {} }: { headers?: Record<string, string> }) => ({
            headers: {
              ...headers,
              'flair-locale': getLocale(),
              // Local dev serves each hub on a <instance>.localhost subdomain,
              // but the Vite proxy to the API drops it, so the server can't
              // resolve the visited hub and falls back to the org-level
              // Salesforce connection. Forward it explicitly (same as the auth
              // client). In production the request is same-origin, so the real
              // Host already carries it.
              ...(devMode ? { 'x-forwarded-host': window.location.host } : {}),
            },
          }),
        );
        return forward(operation);
      }),
      onError((errorResponse) => {
        addRequestInfoToErrorResponse(errorResponse);
      }),
      createRefreshJwtTokenLink(logout),
      new HttpLink({
        uri: getApiBaseUrl() + '/internal/graphql',
      }),
    ]),
    cache: new InMemoryCache(apolloCacheConfig),
  });

export const Provider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { isLoggedIn, logout } = useAuth();

  // Keep the latest logout in a ref so the client (built once) always calls the
  // current one without being rebuilt. The locale is read per request directly
  // from i18next via getCurrentLocale, so it needs no ref.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const clientRef = useRef<ApolloClient<NormalizedCacheObject>>();
  if (!clientRef.current) {
    clientRef.current = buildClient(
      () => logoutRef.current(),
      getCurrentLocale,
    );
  }

  if (isLoggedIn === false) {
    logout();
    return <></>;
  }

  return <ApolloProvider client={clientRef.current} children={children} />;
};
